import React, { useState, useEffect, createContext, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  Container,
  Box,
  CssBaseline,
  ThemeProvider,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Fade,
} from "@mui/material";
import NavBar from "./components/NavBar";
import axios from "axios";
import getTheme from "./theme";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider } from "./context/ToastContext";

const MainPage = lazy(() => import("./pages/MainPage"));
const ChatbotPage = lazy(() => import("./pages/ChatbotPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DataManagement = lazy(() => import("./pages/DataManagement"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const WhatsAppPage = lazy(() => import("./pages/WhatsAppPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const EmbedPage = lazy(() => import("./pages/EmbedPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));

export const ColorModeContext = createContext({ toggleColorMode: () => {}, colorMode: "light" });

const SuspenseFallback = () => (
  <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
    <CircularProgress />
  </Box>
);

const FadePage = ({ children }) => (
  <Fade in timeout={300}>
    <div>{children}</div>
  </Fade>
);

const AppContent = ({ user, isLoggedIn, handleLogin, handleLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const themeLocal = useTheme();
  const isMobile = useMediaQuery(themeLocal.breakpoints.down("sm"));

  const searchParams = new URLSearchParams(location.search);
  const isEmbed = searchParams.get("embed") === "true";

  // Anonymous visit counter: once per browser session, only from real browsers (bots that
  // don't run JavaScript never get here). Sends no identifier; failures are ignored.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("visit_logged")) return;
      sessionStorage.setItem("visit_logged", "1");
    } catch {
      /* storage blocked: count this page load, can't dedupe */
    }
    axios.post("/api/usage/visit/", { embed: isEmbed }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmbed && isMobile && location.pathname !== "/chatbot") {
      navigate("/chatbot", { replace: true });
    }
  }, [isEmbed, isMobile, location.pathname, navigate]);

  return (
    <>
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          left: "-9999px",
          zIndex: 9999,
          p: 1,
          backgroundColor: "background.paper",
          color: "text.primary",
          "&:focus": { left: 0, top: 0 },
        }}
      >
        Skip to main content
      </Box>

      {!isEmbed && <NavBar isLoggedIn={isLoggedIn} isAdmin={!!(user?.is_staff || user?.is_superuser)} onLogout={handleLogout} />}
      <Container id="main-content" maxWidth={false} disableGutters>
        <Box sx={{ width: "80%", margin: "0 auto", mt: themeLocal.spacing(4) }}>
          <Suspense fallback={<SuspenseFallback />}>
            <Routes>
              <Route
                path="/"
                element={<FadePage><MainPage isLoggedIn={isLoggedIn} /></FadePage>}
              />
              <Route
                path="/chatbot"
                element={<FadePage><ChatbotPage /></FadePage>}
              />
              <Route
                path="/login"
                element={
                  isLoggedIn ? (
                    <Navigate to="/dashboard" />
                  ) : (
                    <FadePage><LoginPage onLogin={handleLogin} /></FadePage>
                  )
                }
              />
              <Route
                path="/dashboard"
                element={
                  isLoggedIn ? (
                    <FadePage><Dashboard user={user} onLogout={handleLogout} /></FadePage>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/data-management"
                element={
                  isLoggedIn ? (
                    <FadePage><DataManagement /></FadePage>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/platform-admin"
                element={
                  isLoggedIn ? (
                    <FadePage><AdminPage user={user} /></FadePage>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/about"
                element={<FadePage><AboutPage /></FadePage>}
              />
              <Route
                path="/privacy"
                element={<FadePage><PrivacyPage /></FadePage>}
              />
              <Route
                path="/whatsapp"
                element={<FadePage><WhatsAppPage /></FadePage>}
              />
              <Route
                path="/how-it-works"
                element={<FadePage><HowItWorksPage /></FadePage>}
              />
              <Route
                path="/embed"
                element={<FadePage><EmbedPage /></FadePage>}
              />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </Box>
      </Container>
    </>
  );
};

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const App = () => {
  // Seed from the cached user so the name paints immediately on reload instead of flashing blank.
  const [user, setUser] = useState(readCachedUser);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("token"));
  const navigate = useNavigate();

  // Revalidate the token in the background; refresh the cached user or sign out if it's stale.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios
      .get("/api/users/me/", { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        setUser(response.data);
        localStorage.setItem("user", JSON.stringify(response.data));
        setIsLoggedIn(true);
      })
      .catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("refresh");
        localStorage.removeItem("user");
        setUser(null);
        setIsLoggedIn(false);
      });
  }, []);

  const handleLogin = async (username, password) => {
    try {
      const response = await axios.post(
        "/api/login/",
        { username, password },
        { headers: { "Content-Type": "application/json" } }
      );
      const { access, refresh, user: userData } = response.data;
      localStorage.setItem("token", access);
      localStorage.setItem("refresh", refresh);
      if (userData) localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData || null);
      setIsLoggedIn(true);
      // Navigation is done by the caller (LoginPage) once this resolves.
    } catch (err) {
      console.error("Login Error:", err.response ? err.response.data : err);
      throw new Error("Invalid credentials. Try again.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh");
    localStorage.removeItem("user");
    setUser(null);
    setIsLoggedIn(false);
    navigate("/");
  };

  return (
    <AppContent
      user={user}
      isLoggedIn={isLoggedIn}
      handleLogin={handleLogin}
      handleLogout={handleLogout}
    />
  );
};

const AppWrapper = () => (
  <Router>
    <App />
  </Router>
);

const ThemedApp = () => {
  const [colorMode, setColorMode] = useState(
    () => localStorage.getItem("colorMode") || "light"
  );

  const toggleColorMode = () => {
    setColorMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("colorMode", next);
      return next;
    });
  };

  return (
    <ColorModeContext.Provider value={{ toggleColorMode, colorMode }}>
      <ThemeProvider theme={getTheme(colorMode)}>
        <CssBaseline />
        <ToastProvider>
          <ErrorBoundary>
            <AppWrapper />
          </ErrorBoundary>
        </ToastProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default ThemedApp;
